import { MouseEvent, useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import { FundraisingModel, CampaignMessageTemplate } from 'src/models/FundraisingModel';
import { renderHasChanged } from 'src/components/Pages/AddFundraisingPage';
import { useAppDispatch } from 'src/redux/Store';
import { FORM } from 'src/constants/Form';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import FormRichTextEditor from 'src/components/Control/FormControls/FormRichTextEditor';
import Row from 'src/components/UI/Row';
import Subtitle from 'src/components/UI/Subtitle';
import Scroller from 'src/components/UI/Scroller';
import BottomRow from 'src/components/UI/BottomRow';
import FundraiserTitle from 'src/components/UI/FundRaiserTitle';
import InlineHelp from 'src/components/UI/InlineHelp';
import Line from 'src/components/UI/Line';
import { COLORS } from 'src/constants/Color';
import { putFundraisingCampaignMessageAction } from 'src/redux/actions/fundraisingAction';

interface Props {
  fundraisingState: FundraisingModel;
  hasChanged?: boolean;
  setHasChanged: (hasChanged: boolean) => void;
}

const EMPTY_CAMPAIGN_MESSAGE: CampaignMessageTemplate = {
  campaign_message_email_title: '',
  campaign_message_email_body: '',
  campaign_message_sms_text: '',
};

const FundraisingCampaignMessageForm = (props: Props) => {
  const {
    fundraisingState,
    hasChanged,
    setHasChanged,
  } = props;
  const dispatch = useAppDispatch();

  const getInitialCampaignMessage = (): CampaignMessageTemplate => {
    if (fundraisingState.campaignMessage) {
      return { ...fundraisingState.campaignMessage };
    }
    return { ...EMPTY_CAMPAIGN_MESSAGE };
  };

  const [campaignMessage, setCampaignMessage] = useState<CampaignMessageTemplate>(
    getInitialCampaignMessage(),
  );

  useEffect(() => {
    if (!fundraisingState.campaignMessage) return;
    setCampaignMessage({ ...fundraisingState.campaignMessage });
  }, [fundraisingState]);

  const onValueChange = (value: string, field: string) => {
    setHasChanged(true);
    setCampaignMessage({
      ...campaignMessage,
      [field]: value,
    });
  };

  const renderMaxWord = (field: keyof CampaignMessageTemplate, max: number) => {
    const wordCount = campaignMessage[field]?.length ?? 0;
    return (
      <div style={{
        fontSize: '12px',
        marginTop: '-8px',
        marginBottom: '16px',
        color: COLORS.darkgrey,
        marginLeft: '0px',
      }}>
        {`${wordCount}/${max} characters`}
      </div>
    );
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const fundraisingId = fundraisingState?.fundraisingInfo?.id;
    if (!fundraisingId) return;
    dispatch(
      putFundraisingCampaignMessageAction(
        fundraisingId,
        campaignMessage,
        (newState: FundraisingModel) => {
          if (newState.campaignMessage) {
            setCampaignMessage({ ...newState.campaignMessage });
          }
          setHasChanged(false);
        },
      ),
    );
  };

  return (
    <>
      <FundraiserTitle>Campaign Message</FundraiserTitle>
      <Scroller height="565px">
        <Row alignItems="start">
          <div style={{ width: '50%', paddingTop: '8px' }}>
            <Subtitle style={{ marginTop: '8px', marginBottom: '16px' }}>
              Email message
            </Subtitle>
            <FormTextField
              form={FORM.fundraisingCampaignMessage}
              style={{ marginTop: '0px' }}
              label="Email title"
              name="campaign_message_email_title"
              onChange={onValueChange}
              margin="dense"
              inputProps={{ maxLength: 255 }}
              value={campaignMessage.campaign_message_email_title} />
            <FormRichTextEditor
              form={FORM.fundraisingCampaignMessage}
              toolbar="invitation"
              label="Text for email"
              name="campaign_message_email_body"
              onChange={onValueChange}
              height="200px"
              value={campaignMessage.campaign_message_email_body} />
            <InlineHelp>
              <Line>Please use the following tags in your message:</Line>
              <Line>
                <i>#first-name</i>,&nbsp;
                <i>#last-name</i>,&nbsp;
                <i>#company_name</i>,&nbsp;
                <i>#organization-name</i>,&nbsp;
                <i>#campaign_name</i>,&nbsp;
                <i>#campaign-link</i>,&nbsp;
                <i>#amount</i>
              </Line>
              <Line>The system will automatically replace them when the message is sent.</Line>
              <Line><i>Example:</i></Line>
              <Line>Hello #first-name</Line>
              <Line>&nbsp;</Line>
              <Line>Thanks for your gift of $#amount to #campaign_name: #campaign-link</Line>
            </InlineHelp>
          </div>
          <div style={{ width: '50%', paddingTop: '8px' }}>
            <Subtitle style={{ marginTop: '8px', marginBottom: '16px' }}>
              SMS message
            </Subtitle>
            <FormTextField
              form={FORM.fundraisingCampaignMessage}
              style={{ marginTop: '0px' }}
              label="Text for SMS (max 1200 chars)"
              name="campaign_message_sms_text"
              onChange={onValueChange}
              multiline
              minRows={5}
              maxRows={5}
              inputProps={{ maxLength: 1200 }}
              value={campaignMessage.campaign_message_sms_text} />
            {renderMaxWord('campaign_message_sms_text', 1200)}
          </div>
        </Row>
        <BottomRow>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => handleSubmit(e)}
            key="submit-button">
            Save{renderHasChanged(!hasChanged)}
          </Button>
        </BottomRow>
      </Scroller>
    </>
  );
};

export default FundraisingCampaignMessageForm;
